import { ANALYTICS_EVENTS, ANALYTICS_PROPERTY_KEYS, COMPONENT_OUTCOMES, CREATION_TYPES, DCM_PAGES } from '../constants';
import { trackAnalyticsEvent } from '../core/analyticsClient';

type CreationType = (typeof CREATION_TYPES)[keyof typeof CREATION_TYPES];

const buildCreationTypeProps = ({
  creationType = CREATION_TYPES.NEW,
  sourceCampaignId,
  sourceLineItemId,
}: {
  creationType?: CreationType;
  sourceCampaignId?: string;
  sourceLineItemId?: string;
}): Record<string, string> => ({
  [ANALYTICS_PROPERTY_KEYS.CREATION_TYPE]: creationType,
  ...(sourceCampaignId ? { [ANALYTICS_PROPERTY_KEYS.SOURCE_CAMPAIGN_ID]: sourceCampaignId } : {}),
  ...(sourceLineItemId ? { [ANALYTICS_PROPERTY_KEYS.SOURCE_LINE_ITEM_ID]: sourceLineItemId } : {}),
});

export type TrackLineItemCreatedParams = {
  campaignId: string;
  lineItemId: string;
  adAccountId?: string;
  organizationId?: string;
  creationType?: CreationType;
  sourceCampaignId?: string;
  sourceLineItemId?: string;
};

export const trackLineItemCreated = ({
  campaignId,
  lineItemId,
  adAccountId,
  organizationId,
  creationType,
  sourceCampaignId,
  sourceLineItemId,
}: TrackLineItemCreatedParams): void => {
  trackAnalyticsEvent(ANALYTICS_EVENTS.LINE_ITEM_CREATED, {
    [ANALYTICS_PROPERTY_KEYS.CAMPAIGN_ID]: campaignId,
    [ANALYTICS_PROPERTY_KEYS.LINE_ITEM_ID]: lineItemId,
    [ANALYTICS_PROPERTY_KEYS.AD_ACCOUNT_ID]: adAccountId,
    [ANALYTICS_PROPERTY_KEYS.ORGANIZATION_ID]: organizationId,
    [ANALYTICS_PROPERTY_KEYS.PAGE]: DCM_PAGES.CAMPAIGN_BUILDER,
    [ANALYTICS_PROPERTY_KEYS.COMPONENT_OUTCOME]: COMPONENT_OUTCOMES.USER,
    ...buildCreationTypeProps({ creationType, sourceCampaignId, sourceLineItemId }),
  });
};

export type TrackLineItemSubmittedParams = {
  campaignId: string;
  lineItemId: string;
  adAccountId?: string;
  organizationId?: string;
};

export const trackLineItemSubmitted = ({
  campaignId,
  lineItemId,
  adAccountId,
  organizationId,
}: TrackLineItemSubmittedParams): void => {
  trackAnalyticsEvent(ANALYTICS_EVENTS.LINE_ITEM_SUBMITTED, {
    [ANALYTICS_PROPERTY_KEYS.CAMPAIGN_ID]: campaignId,
    [ANALYTICS_PROPERTY_KEYS.LINE_ITEM_ID]: lineItemId,
    [ANALYTICS_PROPERTY_KEYS.AD_ACCOUNT_ID]: adAccountId,
    [ANALYTICS_PROPERTY_KEYS.ORGANIZATION_ID]: organizationId,
    [ANALYTICS_PROPERTY_KEYS.PAGE]: DCM_PAGES.CAMPAIGN_PAYMENT,
    [ANALYTICS_PROPERTY_KEYS.COMPONENT_OUTCOME]: COMPONENT_OUTCOMES.USER,
  });
};

export type TrackCampaignCreatedParams = {
  campaignId: string;
  adAccountId: string;
  campaignName?: string;
  creationType?: CreationType;
  sourceCampaignId?: string;
};

export const trackCampaignCreated = ({
  campaignId,
  adAccountId,
  campaignName,
  creationType,
  sourceCampaignId,
}: TrackCampaignCreatedParams): void => {
  trackAnalyticsEvent(ANALYTICS_EVENTS.CAMPAIGN_CREATED, {
    [ANALYTICS_PROPERTY_KEYS.CAMPAIGN_ID]: campaignId,
    [ANALYTICS_PROPERTY_KEYS.AD_ACCOUNT_ID]: adAccountId,
    [ANALYTICS_PROPERTY_KEYS.PAGE]: DCM_PAGES.CAMPAIGN_CREATE,
    [ANALYTICS_PROPERTY_KEYS.COMPONENT_OUTCOME]: COMPONENT_OUTCOMES.USER,
    ...(campaignName ? { [ANALYTICS_PROPERTY_KEYS.CAMPAIGN_NAME]: campaignName } : {}),
    ...buildCreationTypeProps({ creationType, sourceCampaignId }),
  });
};

export type TrackCampaignSubmittedParams = {
  campaignId: string;
  adAccountId: string;
  organizationId?: string;
};

export const trackCampaignSubmitted = ({
  campaignId,
  adAccountId,
  organizationId,
}: TrackCampaignSubmittedParams): void => {
  trackAnalyticsEvent(ANALYTICS_EVENTS.CAMPAIGN_SUBMITTED, {
    [ANALYTICS_PROPERTY_KEYS.CAMPAIGN_ID]: campaignId,
    [ANALYTICS_PROPERTY_KEYS.AD_ACCOUNT_ID]: adAccountId,
    [ANALYTICS_PROPERTY_KEYS.ORGANIZATION_ID]: organizationId,
    [ANALYTICS_PROPERTY_KEYS.PAGE]: DCM_PAGES.CAMPAIGN_PAYMENT,
    [ANALYTICS_PROPERTY_KEYS.COMPONENT_OUTCOME]: COMPONENT_OUTCOMES.USER,
  });
};
